import { useState, type ComponentType } from "react";
import { usePrefixedNavigate } from "../hooks/usePrefixedNavigate";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  authAPI,
  profilesAPI,
  userRolesAPI,
  donorsAPI,
  trusteesAPI,
  hostelsAPI,
} from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import {
  Home,
  GraduationCap,
  Building2,
  Users,
  Heart,
  Shield,
  Smartphone,
  KeyRound,
  UserPlus,
  LogIn,
  CheckCircle2,
  Clock,
  Award,
  Users2,
  ShieldCheck,
  Mail,
  Lock,
  Eye,
  EyeOff,
} from "lucide-react";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "../components/ui/input-otp";
import { Badge } from "../components/ui/badge";

type PortalType =
  | "student"
  | "hostel"
  | "institution"
  | "trustee"
  | "donor"
  | "super_admin";

interface PortalConfig {
  icon: ComponentType<{ className?: string }>;
  title: string;
  subtitle: string;
  description: string;
  role: string;
  color: string;
  gradient: string;
  dashboardPath: string;
  onboardingPath?: string;
  testPhone?: string;
  features: string[];
}

const portalConfigs: Record<PortalType, PortalConfig> = {
  student: {
    icon: GraduationCap,
    title: "Student Portal",
    subtitle: "Access your education journey",
    description:
      "Register and apply to hostels, track your applications, and manage your academic profile all in one place.",
    role: "student",
    color: "bg-emerald-600",
    gradient: "from-emerald-600 to-emerald-800",
    dashboardPath: "/dashboard/student",
    onboardingPath: "/student/register",
    testPhone: "9000000001",
    features: [
      "Apply to Hostels",
      "Track Applications",
      "View Scholarships",
      "Academic Records",
    ],
  },
  hostel: {
    icon: Building2,
    title: "Hostel Admin Portal",
    subtitle: "Manage your hostel operations",
    description:
      "Register your hostel, manage students, handle admissions, and streamline your hostel operations efficiently.",
    role: "hostel_admin",
    color: "bg-blue-600",
    gradient: "from-blue-600 to-blue-800",
    dashboardPath: "/dashboard/hostel-admin",
    onboardingPath: "/hostel/onboarding",
    testPhone: "9000000002",
    features: [
      "Student Management",
      "Room Allocation",
      "Fee Collection",
      "Reports & Analytics",
    ],
  },
  institution: {
    icon: Building2,
    title: "Institution Admin Portal",
    subtitle: "Manage your institution",
    description:
      "Complete institution management system for courses, students, staff, attendance, and comprehensive reporting.",
    role: "institutions_admin",
    color: "bg-purple-600",
    gradient: "from-purple-600 to-purple-800",
    dashboardPath: "/institution-admin/dashboard",
    onboardingPath: "/institution/onboarding",
    testPhone: "9000000003",
    features: [
      "Course Management",
      "Staff & Students",
      "Attendance Tracking",
      "Document Management",
    ],
  },
  trustee: {
    icon: Users,
    title: "Trustee Portal",
    subtitle: "Oversee institutional governance",
    description:
      "Monitor institutions, manage scholarships, approve loans, and ensure compliance with governance policies.",
    role: "trustee",
    color: "bg-amber-600",
    gradient: "from-amber-600 to-amber-800",
    dashboardPath: "/dashboard/trustee",
    testPhone: "9000000004",
    features: [
      "Institution Oversight",
      "Scholarship Approval",
      "Loan Management",
      "Governance Reports",
    ],
  },
  donor: {
    icon: Heart,
    title: "Donor Portal",
    subtitle: "Make a difference",
    description:
      "Track your donations, view impact reports, download tax receipts, and see how your contributions help students.",
    role: "donor",
    color: "bg-rose-600",
    gradient: "from-rose-600 to-rose-800",
    dashboardPath: "/dashboard/donor",
    testPhone: "9000000005",
    features: [
      "Donation Tracking",
      "Impact Reports",
      "Tax Receipts",
      "Volunteer Opportunities",
    ],
  },
  super_admin: {
    icon: Shield,
    title: "Admin Portal",
    subtitle: "Complete system control",
    description:
      "Full administrative access to manage all portals, users, content, approvals, and system-wide configurations.",
    role: "super_admin",
    color: "bg-slate-800",
    gradient: "from-slate-700 to-slate-900",
    dashboardPath: "/super-admin/dashboard",
    testPhone: "9000000000",
    features: [
      "User Management",
      "Approval Workflow",
      "System Settings",
      "All Portal Access",
    ],
  },
};

export default function Auth() {
  const navigate = usePrefixedNavigate();
  const { updateAuth } = useAuth();
  const rootNavigate = useNavigate();
  const [searchParams] = useSearchParams();
  const portalType = (searchParams.get("portal") ?? "student") as PortalType;

  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [codeReference, setCodeReference] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<"phone" | "otp">("phone");
  const [authMode, setAuthMode] = useState<"login" | "register">("login");
  const [showStudentSubmittedModal, setShowStudentSubmittedModal] =
    useState(false);
  // Super admin: email/password login only (no OTP, no register)
  const [superAdminEmail, setSuperAdminEmail] = useState("");
  const [superAdminPassword, setSuperAdminPassword] = useState("");
  const [showSuperAdminPassword, setShowSuperAdminPassword] = useState(false);

  const config = portalConfigs[portalType] ?? portalConfigs.student;
  const PortalIcon = config.icon;

  const handleSendOtp = async () => {
    if (phone.length < 10) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }

    setLoading(true);
    try {
      // Normalize phone number (remove spaces, ensure 10 digits)
      const normalizedPhone = phone.replace(/\D/g, "").slice(0, 10);

      if (normalizedPhone.length !== 10) {
        toast.error("Please enter a valid 10-digit mobile number");
        setLoading(false);
        return;
      }

      // Use phone-based OTP (same endpoint as karuna project)
      // phone_code is a UUID identifier - using the same one from karuna project
      const phoneCode = import.meta.env.VITE_PHONE_CODE as string;

      const response = await authAPI.sendOTP(
        normalizedPhone,
        phoneCode,
        authMode === "register"
      );

      // Store code_reference - REQUIRED for verification
      if (response?.code_reference) {
        setCodeReference(response.code_reference);
        console.log("✅ Stored code_reference:", response.code_reference);
      } else {
        throw new Error("No code_reference received from server");
      }

      toast.success("OTP sent to +91" + normalizedPhone);
      setStep("otp");
    } catch (error: any) {
      console.error("Send OTP error:", error);
      console.error("Error response:", error?.response?.data);
      const errorMessage =
        error?.response?.data?.message ??
        error?.response?.data?.error?.message ??
        error?.message ??
        "Failed to send OTP. Please try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (phone.length < 10) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }

    if (!codeReference) {
      toast.error("Code reference missing. Please send OTP again.");
      return;
    }

    setLoading(true);
    try {
      // Normalize phone number
      const normalizedPhone = phone.replace(/\D/g, "").slice(0, 10);
      const phoneCode = import.meta.env.VITE_PHONE_CODE as string;

      // Resend OTP - can use same sendOTP endpoint or resend-otp endpoint
      // Try resend-otp first, fallback to sendOTP
      try {
        const response = await authAPI.resendOTP(
          normalizedPhone,
          codeReference
        );
        if (response?.code_reference) {
          setCodeReference(response.code_reference);
        }
        toast.success("OTP resent successfully");
      } catch (_resendError) {
        // If resend-otp doesn't work, use sendOTP again (with role and register flag)
        const response = await authAPI.sendOTP(
          normalizedPhone,
          phoneCode,
          authMode === "register"
        );
        if (response?.code_reference) {
          setCodeReference(response.code_reference);
        }
        toast.success("OTP sent again");
      }
    } catch (error: any) {
      console.error("Resend OTP error:", error);
      const errorMessage =
        error?.response?.data?.message ??
        error?.response?.data?.error?.message ??
        error?.message ??
        "Failed to resend OTP. Please try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndLogin = async () => {
    if (otp.length < 6) {
      toast.error("Please enter the 6-digit OTP");
      return;
    }

    setLoading(true);
    try {
      if (authMode === "login") {
        // Verify OTP and login - requires code_reference
        if (!codeReference) {
          throw new Error("Code reference is missing. Please send OTP again.");
        }

        const authResponse = await authAPI.verifyOTP(otp, codeReference, false);

        if (!authResponse) {
          throw new Error("Login failed");
        }

        // Update auth context with user data and tokens.
        // Include jbf_role from the top level of the response if available.
        // updateAuth now returns the merged user profile from the my-profile API.
        const fullProfile: any = await updateAuth(
          authResponse.user,
          authResponse.access,
          authResponse.refresh,
          authResponse.jbf_role
        );

        // Use role from login response (prioritize top-level jbf_role, fallback to user.jbf_role)
        const role =
          authResponse.jbf_role ?? authResponse.user.jbf_role ?? null;

        if (role) {
          if (role === "institutions_admin") {
            // Use my-profile response from updateAuth — no extra API call
            const data = fullProfile?.data ?? fullProfile;
            const raw = data?.has_institution;
            const hasInstitution =
              raw === true || String(raw).toLowerCase() === "true";
            if (hasInstitution) {
              navigate("/institution-admin/dashboard");
              return;
            }
            navigate("/institution/onboarding");
            return;
          }

          toast.success("Welcome back!");
          switch (role) {
            case "student": {
              // Use student profile data directly from the merged user object
              const studentProfile = fullProfile;
              const formSubmitted = studentProfile?.form_submitted;

              if (formSubmitted === true) {
                setShowStudentSubmittedModal(true);
              } else if (formSubmitted === false || !studentProfile) {
                navigate("/student/register");
              } else {
                // Determine if a record exists in the profile
                const hasInquiry =
                  studentProfile?.full_name ?? studentProfile?.id;
                navigate(
                  hasInquiry ? "/dashboard/student" : "/student/register"
                );
              }
              break;
            }
            case "parent":
              navigate("/dashboard/student");
              break;
            case "hostel_admin":
              const hostels = await hostelsAPI.getByUserId(
                authResponse.user.id
              );
              const hostel =
                Array.isArray(hostels) && hostels.length > 0
                  ? hostels[0]
                  : null;
              navigate(
                hostel ? "/dashboard/hostel-admin" : "/hostel/onboarding"
              );
              break;
            case "trustee":
              navigate("/dashboard/trustee");
              break;
            case "donor":
              navigate("/dashboard/donor");
              break;
            case "super_admin":
            case "management":
              navigate("/super-admin/dashboard");
              break;
            default:
              navigate(config.dashboardPath);
          }
        } else {
          // No role in response: for institution portal still use has_institution from my-profile
          if (portalType === "institution" && fullProfile) {
            const data = fullProfile?.data ?? fullProfile;
            const raw = data?.has_institution;
            const hasInstitution =
              raw === true || String(raw).toLowerCase() === "true";
            if (hasInstitution) {
              navigate("/institution-admin/dashboard");
              return;
            }
            navigate("/institution/onboarding");
            return;
          }
          navigate(config.onboardingPath ?? config.dashboardPath);
        }
      } else {
        // Register new user
        const prefix =
          portalType === "student"
            ? "student"
            : portalType === "hostel"
              ? "hostel"
              : portalType === "institution"
                ? "institution"
                : portalType === "trustee"
                  ? "trustee"
                  : portalType === "donor"
                    ? "donor"
                    : "admin";

        const email = `${prefix}_${phone}@jbgportal.temp`;
        const password = `${prefix}_${phone}_secure`;

        // Verify OTP first before signup - requires code_reference
        if (!codeReference) {
          throw new Error("Code reference is missing. Please send OTP again.");
        }

        const verifyResponse = await authAPI.verifyOTP(
          otp,
          codeReference,
          true
        );

        if (!verifyResponse) {
          throw new Error("OTP verification failed");
        }

        // After OTP verification: use response from /auth/ if it returned user + tokens; else signup
        try {
          const authResponse =
            verifyResponse?.user && verifyResponse?.access
              ? verifyResponse
              : await authAPI.signup({
                  email,
                  password,
                  phone: `+91${phone}`,
                  user_type: config.role,
                });

          if (!authResponse) {
            throw new Error("Registration failed - no response received");
          }

          // Update auth context
          // Include jbf_role from verifyResponse/signup
          await updateAuth(
            authResponse.user,
            authResponse.access,
            authResponse.refresh,
            authResponse.jbf_role
          );

          // Create profile
          await profilesAPI.upsert({
            user_id: authResponse.user.id,
            phone: `+91${phone}`,
          });

          // Create user role
          await userRolesAPI.create({
            user_id: authResponse.user.id,
            jbf_role: config.role,
          });

          // Create portal-specific records
          if (portalType === "donor") {
            await donorsAPI.create({
              user_id: authResponse.user.id,
            });
          } else if (portalType === "trustee") {
            await trusteesAPI.create({
              user_id: authResponse.user.id,
            });
          }
          // Re-update auth context after creating role/profile so fetchUserRole/getMyProfile can succeed
          await updateAuth(
            authResponse.user,
            authResponse.access,
            authResponse.refresh,
            authResponse.jbf_role ?? config.role
          );

          toast.success("Account created! Redirecting...");
          navigate(
            `/jainboarding-admin${config.onboardingPath ?? config.dashboardPath}`
          );
        } catch (signupError: any) {
          // Handle signup-specific errors
          console.error("Signup error:", signupError);
          throw signupError;
        }
      }
    } catch (error: any) {
      console.error("Auth error:", error);
      console.error("Error response:", error?.response);
      console.error("Request URL:", error?.config?.url);
      console.error("Full URL:", error?.config?.baseURL + error?.config?.url);

      const errorMessage =
        error?.response?.data?.message ??
        error?.response?.data?.error?.message ??
        error?.message ??
        "Something went wrong. Please try again.";

      if (
        errorMessage.includes("not found") ||
        errorMessage.includes("Invalid")
      ) {
        toast.error("Account not found. Please register first.");
        setAuthMode("register");
        setStep("phone");
      } else if (
        errorMessage.includes("already exists") ||
        errorMessage.includes("already registered")
      ) {
        toast.error("Account already exists. Please login instead.");
        setAuthMode("login");
        setStep("phone");
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBackToPhone = () => {
    setStep("phone");
    setOtp("");
  };

  const handleSuperAdminLogin = async () => {
    const email = superAdminEmail.trim();
    const password = superAdminPassword;
    if (!email) {
      toast.error("Please enter your email");
      return;
    }
    if (!password) {
      toast.error("Please enter your password");
      return;
    }
    setLoading(true);
    try {
      const authResponse = await authAPI.login(email, password);
      if (!authResponse) {
        throw new Error("Login failed");
      }
      await updateAuth(
        authResponse.user,
        authResponse.access,
        authResponse.refresh,
        authResponse.jbf_role
      );
      // const role = authResponse.user.jbf_role ?? null;

      navigate("/super-admin/dashboard");
      // if (role === "super_admin") {
      //   toast.success("Welcome back!");
      //   navigate("/dashboard/super-admin");
      // } else {
      //   toast.error("Access denied. Super admin only.");
      // }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ??
        error?.response?.data?.error?.message ??
        error?.message ??
        "Login failed. Please check your email and password.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const isSuperAdmin = portalType === "super_admin";

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur-sm">
        <div className="container mx-auto flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            {/* <Button
              variant="ghost"
              size="icon"
              onClick={() =>
                step === "otp"
                  ? handleBackToPhone()
                  : navigate("/portal-selection")
              }
              className="hover:bg-muted"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button> */}
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary">
                <Home className="h-5 w-5 text-primary-foreground" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-foreground">
                  JBF Portal
                </h1>
                <p className="text-xs text-muted-foreground">
                  Jain Boarding Group
                </p>
              </div>
            </div>
          </div>
          <Badge
            variant="outline"
            className="hidden gap-1.5 px-3 py-1.5 sm:flex"
          >
            <ShieldCheck className="h-3.5 w-3.5 text-green-600" />
            <span className="text-xs">Secure Login</span>
          </Badge>
        </div>
      </header>

      <main className="flex flex-1">
        {/* Left Side - Branding & Info */}
        <div
          className={`hidden bg-gradient-to-br lg:flex lg:w-1/2 ${config.gradient} relative flex-col justify-between overflow-hidden p-12 text-white`}
        >
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute left-0 top-0 h-96 w-96 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white" />
            <div className="absolute bottom-0 right-0 h-80 w-80 translate-x-1/3 translate-y-1/3 rounded-full bg-white" />
          </div>

          <div className="relative z-10">
            <div
              className={`mb-8 flex h-20 w-20 items-center justify-center rounded-2xl bg-white/20 backdrop-blur-sm`}
            >
              <PortalIcon className="h-10 w-10 text-white" />
            </div>
            <h2 className="mb-4 text-4xl font-bold">{config.title}</h2>
            <p className="mb-2 text-xl text-white/90">{config.subtitle}</p>
            <p className="max-w-md leading-relaxed text-white/70">
              {config.description}
            </p>
          </div>

          <div className="relative z-10 space-y-6">
            <div>
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-white/60">
                Key Features
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {config.features.map(feature => (
                  <div
                    key={feature}
                    className="flex items-center gap-2 text-white/90"
                  >
                    <CheckCircle2 className="h-4 w-4 text-white/70" />
                    <span className="text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-white/20 pt-6">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Award className="h-5 w-5 text-white/70" />
                  <span className="text-sm text-white/80">
                    Trusted Platform
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Users2 className="h-5 w-5 text-white/70" />
                  <span className="text-sm text-white/80">10,000+ Users</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Auth Form */}
        <div className="flex flex-1 items-center justify-center bg-gradient-to-br from-background to-muted/30 p-6 lg:p-12">
          <div className="w-full max-w-md">
            {/* Mobile Portal Header */}
            <div className="mb-8 text-center lg:hidden">
              <div
                className={`h-16 w-16 rounded-2xl ${config.color} mx-auto mb-4 flex items-center justify-center shadow-lg`}
              >
                <PortalIcon className="h-8 w-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-foreground">
                {config.title}
              </h2>
              <p className="mt-1 text-muted-foreground">{config.subtitle}</p>
            </div>

            <Card className="border-0 bg-card/80 shadow-xl backdrop-blur-sm">
              <CardHeader className="space-y-1 pb-4">
                <CardTitle className="text-center text-2xl">
                  {isSuperAdmin
                    ? "Admin Login"
                    : step === "phone"
                      ? authMode === "login"
                        ? "Welcome Back"
                        : "Create Account"
                      : "Verify OTP"}
                </CardTitle>
                <CardDescription className="text-center">
                  {isSuperAdmin
                    ? "Sign in with your email and password"
                    : step === "phone"
                      ? authMode === "login"
                        ? "Enter your registered mobile number to continue"
                        : "Register with your mobile number to get started"
                      : `Enter the 6-digit code sent to +91 ${phone}`}
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-6">
                {isSuperAdmin ? (
                  /* Super Admin: Email + Password only, no Register tab */
                  <>
                    <div className="space-y-3">
                      <Label
                        htmlFor="super-admin-email"
                        className="flex items-center gap-2 text-sm font-medium"
                      >
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        Email
                      </Label>
                      <Input
                        id="super-admin-email"
                        type="email"
                        placeholder="Enter your email"
                        value={superAdminEmail}
                        onChange={e => setSuperAdminEmail(e.target.value)}
                        className="h-11 text-base"
                        onKeyDown={e =>
                          e.key === "Enter" && handleSuperAdminLogin()
                        }
                        autoComplete="email"
                        autoFocus
                      />
                    </div>
                    <div className="space-y-3">
                      <Label
                        htmlFor="super-admin-password"
                        className="flex items-center gap-2 text-sm font-medium"
                      >
                        <Lock className="h-4 w-4 text-muted-foreground" />
                        Password
                      </Label>
                      <div className="relative">
                        <Input
                          id="super-admin-password"
                          type={showSuperAdminPassword ? "text" : "password"}
                          placeholder="Enter your password"
                          value={superAdminPassword}
                          onChange={e => setSuperAdminPassword(e.target.value)}
                          className="h-11 pr-10 text-base"
                          onKeyDown={e =>
                            e.key === "Enter" && handleSuperAdminLogin()
                          }
                          autoComplete="current-password"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowSuperAdminPassword(prev => !prev)
                          }
                          className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
                          aria-label={
                            showSuperAdminPassword
                              ? "Hide password"
                              : "Show password"
                          }
                        >
                          {showSuperAdminPassword ? (
                            <Eye className="h-4 w-4" />
                          ) : (
                            <EyeOff className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>
                    <Button
                      onClick={handleSuperAdminLogin}
                      className="h-12 w-full text-base font-medium"
                      size="lg"
                      disabled={
                        loading ||
                        !superAdminEmail.trim() ||
                        !superAdminPassword
                      }
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                          Signing in...
                        </span>
                      ) : (
                        <>
                          Login
                          <LogIn className="ml-2 h-4 w-4" />
                        </>
                      )}
                    </Button>
                  </>
                ) : step === "phone" ? (
                  <>
                    {/* Auth Mode Toggle - hidden for super_admin */}
                    <div className="flex rounded-lg bg-muted p-1">
                      <button
                        onClick={() => setAuthMode("login")}
                        className={`flex flex-1 items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm font-medium transition-all ${
                          authMode === "login"
                            ? "bg-background text-foreground shadow-sm"
                            : "text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        <LogIn className="h-4 w-4" />
                        Login
                      </button>
                      <button
                        onClick={() => setAuthMode("register")}
                        className={`flex flex-1 items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm font-medium transition-all ${
                          authMode === "register"
                            ? "bg-background text-foreground shadow-sm"
                            : "text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        <UserPlus className="h-4 w-4" />
                        Register
                      </button>
                    </div>

                    {/* Phone Input */}
                    <div className="space-y-3">
                      <Label
                        htmlFor="phone"
                        className="flex items-center gap-2 text-sm font-medium"
                      >
                        <Smartphone className="h-4 w-4 text-muted-foreground" />
                        Mobile Number
                      </Label>
                      <div className="flex gap-2">
                        <div className="flex items-center rounded-lg border border-input bg-muted px-4">
                          <span className="text-sm font-medium text-muted-foreground">
                            +91
                          </span>
                        </div>
                        <Input
                          id="phone"
                          type="tel"
                          placeholder="Enter 10-digit number"
                          value={phone}
                          onChange={e =>
                            setPhone(
                              e.target.value.replace(/\D/g, "").slice(0, 10)
                            )
                          }
                          className="h-11 flex-1 text-base"
                          onKeyDown={e =>
                            e.key === "Enter" &&
                            phone.length === 10 &&
                            handleSendOtp()
                          }
                          autoFocus
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {authMode === "login"
                          ? "We'll send a one-time password to verify your identity"
                          : "You'll receive an OTP to verify your mobile number"}
                      </p>
                    </div>

                    {/* Phone Number Validation Indicator */}
                    {phone.length > 0 && (
                      <div
                        className={`flex items-center gap-2 text-sm ${phone.length === 10 ? "text-green-600" : "text-amber-600"}`}
                      >
                        {phone.length === 10 ? (
                          <>
                            <CheckCircle2 className="h-4 w-4" />
                            Valid mobile number
                          </>
                        ) : (
                          <>
                            <Clock className="h-4 w-4" />
                            {10 - phone.length} more digits required
                          </>
                        )}
                      </div>
                    )}

                    <Button
                      onClick={handleSendOtp}
                      className="h-12 w-full text-base font-medium"
                      size="lg"
                      disabled={loading || phone.length < 10}
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                          Sending OTP...
                        </span>
                      ) : (
                        <>
                          Send OTP
                          <KeyRound className="ml-2 h-4 w-4" />
                        </>
                      )}
                    </Button>
                  </>
                ) : (
                  <>
                    {/* OTP Verification */}
                    <div className="space-y-4 text-center">
                      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                        <KeyRound className="h-8 w-8 text-primary" />
                      </div>
                    </div>

                    <div className="flex justify-center py-4">
                      <InputOTP
                        maxLength={6}
                        value={otp}
                        onChange={setOtp}
                        className="gap-2"
                      >
                        <InputOTPGroup className="gap-2">
                          <InputOTPSlot
                            index={0}
                            className="h-14 w-12 rounded-lg border-2 text-xl"
                          />
                          <InputOTPSlot
                            index={1}
                            className="h-14 w-12 rounded-lg border-2 text-xl"
                          />
                          <InputOTPSlot
                            index={2}
                            className="h-14 w-12 rounded-lg border-2 text-xl"
                          />
                          <InputOTPSlot
                            index={3}
                            className="h-14 w-12 rounded-lg border-2 text-xl"
                          />
                          <InputOTPSlot
                            index={4}
                            className="h-14 w-12 rounded-lg border-2 text-xl"
                          />
                          <InputOTPSlot
                            index={5}
                            className="h-14 w-12 rounded-lg border-2 text-xl"
                          />
                        </InputOTPGroup>
                      </InputOTP>
                    </div>

                    {/* OTP Validation Indicator */}
                    {otp.length > 0 && (
                      <div
                        className={`flex items-center justify-center gap-2 text-sm ${otp.length === 6 ? "text-green-600" : "text-amber-600"}`}
                      >
                        {otp.length === 6 ? (
                          <>
                            <CheckCircle2 className="h-4 w-4" />
                            Ready to verify
                          </>
                        ) : (
                          <>
                            <Clock className="h-4 w-4" />
                            {6 - otp.length} more digits required
                          </>
                        )}
                      </div>
                    )}

                    <Button
                      onClick={handleVerifyAndLogin}
                      className="h-12 w-full text-base font-medium"
                      size="lg"
                      disabled={loading || otp.length < 6}
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                          Verifying...
                        </span>
                      ) : authMode === "login" ? (
                        "Verify & Login"
                      ) : (
                        "Verify & Create Account"
                      )}
                    </Button>

                    <div className="flex items-center justify-between pt-2 text-sm">
                      <Button
                        variant="link"
                        className="h-auto p-0 text-muted-foreground hover:text-foreground"
                        onClick={handleBackToPhone}
                      >
                        ← Change Number
                      </Button>
                      <Button
                        variant="link"
                        className="h-auto p-0 text-primary"
                        onClick={handleResendOtp}
                        disabled={loading}
                      >
                        Resend OTP
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Terms */}
            <p className="mt-6 px-4 text-center text-xs text-muted-foreground">
              By continuing, you agree to our{" "}
              <a href="#terms" className="text-primary hover:underline">
                Terms of Service
              </a>{" "}
              and{" "}
              <a href="#privacy" className="text-primary hover:underline">
                Privacy Policy
              </a>
            </p>
          </div>
        </div>
      </main>

      {showStudentSubmittedModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-2xl border bg-white p-6 shadow-2xl">
            <div className="mx-auto mt-0.5 w-fit rounded-full bg-green-100 p-2">
              <CheckCircle2 className="h-12 w-12 text-green-600" />
            </div>
            <div className="mb-4 flex items-start gap-3 py-4 text-center">
              <div>
                <h3 className="text-lg font-semibold text-foreground">
                  Form Already Submitted
                </h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Your student admission form is already submitted. Please wait
                  up to 48 hours for our response.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowStudentSubmittedModal(false)}
              >
                Close
              </Button>
              <Button
                onClick={() => {
                  setShowStudentSubmittedModal(false);
                  rootNavigate("/");
                }}
              >
                Go to Site
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Footer - Only visible on mobile */}
      <footer className="border-t bg-background/80 backdrop-blur-sm lg:hidden">
        <div className="container mx-auto px-4 py-4 text-center text-xs text-muted-foreground">
          © 2024 JBF Portal. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
